import { siteApi } from "@api";
import { Paths } from "@constants";
import { WalletTxMethod } from "kz-ui-sdk";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Delay before refetching promotion statuses after deposit success (BE needs time to update)
const PROMOTION_STATUS_REFETCH_DELAY_MS = 1000;
const POLLING_INTERVAL_MS = 1000;
const POLLING_DURATION_MS = 10000;
const MAX_POLLS = 10;

/**
 * Hook to handle post-deposit completion actions (refetching promotion statuses)
 * Can be reused across different deposit success flows
 *
 * @returns Object with onComplete callback to be called when deposit is successful
 */
export function useDepositCompleteHandler() {
  const location = useLocation();
  const [refetchRewardStatus] = siteApi.useLazyGetRewardsStatusQuery();
  const [refetchReferralRewardStatus] = siteApi.useLazyGetReferralRewardStatusQuery();

  // Use regular queries to read current status for eligibility checks
  const { data: rewardStatusRawData } = siteApi.useGetRewardsStatusQuery(undefined, {
    skip: false,
  });
  const { data: referralRewardStatusData } = siteApi.useGetReferralRewardStatusQuery(undefined, {
    skip: false,
  });

  const initialTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getRefetchStrategy = useCallback(() => {
    const pathname = location.pathname;

    if (pathname === Paths.PRIVATE.PRIZES) {
      return { refetchReward: true, refetchReferral: true };
    }

    if (pathname === Paths.PRIVATE.PROMOTION_DAILY_LOGIN || pathname === Paths.PRIVATE.PROMOTION_PLAY_AND_SPIN) {
      return { refetchReward: true, refetchReferral: false };
    }

    if (pathname === Paths.PRIVATE.PROMOTION_REFER_BALLS) {
      return { refetchReward: false, refetchReferral: true };
    }

    // Default: no polling for other routes
    return { refetchReward: false, refetchReferral: false };
  }, [location.pathname]);

  const checkPageSpecificEligibility = useCallback(
    (freshRewardData?: typeof rewardStatusRawData, freshReferralData?: typeof referralRewardStatusData) => {
      const pathname = location.pathname;

      // Use fresh data if provided, otherwise fall back to regular query data
      const rewardData = freshRewardData ?? rewardStatusRawData;
      const referralData = freshReferralData ?? referralRewardStatusData;

      // Check daily bonus eligibility (ineligible when not available)
      const dailyBonusStatus = rewardData?.[WalletTxMethod.DailyBonus];
      const isDailyBonusAvailable = !!dailyBonusStatus;
      const isDailyBonusEligible = isDailyBonusAvailable;

      // Check play and spin eligibility (ineligible when locked)
      const playSpinStatus = rewardData?.[WalletTxMethod.PlaySpin];
      const isPlaySpinLocked = !!playSpinStatus?.enabled && !!playSpinStatus?.lockedInstructions;
      const isPlaySpinEligible = !isPlaySpinLocked;

      // Check referral reward eligibility (eligible when enabled and canParticipate is true)
      const isReferralRewardEligible = !!referralData?.enabled && !!referralData?.canParticipate;

      // Return eligibility based on current page
      if (pathname === Paths.PRIVATE.PROMOTION_DAILY_LOGIN) {
        return isDailyBonusEligible;
      }

      if (pathname === Paths.PRIVATE.PROMOTION_PLAY_AND_SPIN) {
        return isPlaySpinEligible;
      }

      if (pathname === Paths.PRIVATE.PROMOTION_REFER_BALLS) {
        return isReferralRewardEligible;
      }

      if (pathname === Paths.PRIVATE.PRIZES) {
        // For prizes page, check if both daily bonus and play-and-spin are eligible
        return isDailyBonusEligible && isPlaySpinEligible;
      }

      // Default: not eligible (shouldn't reach here for pages that use polling)
      return false;
    },
    [rewardStatusRawData, referralRewardStatusData, location.pathname],
  );

  const checkEligibility = useCallback(
    (strategy: { refetchReward: boolean; refetchReferral: boolean }) => {
      let rewardEligible = true;
      let referralEligible = true;

      if (strategy.refetchReward) {
        const pathname = location.pathname;

        // Check daily bonus eligibility (ineligible when not available)
        const dailyBonusStatus = rewardStatusRawData?.[WalletTxMethod.DailyBonus];
        const isDailyBonusAvailable = !!dailyBonusStatus;
        const isDailyBonusEligible = isDailyBonusAvailable;

        // Check play and spin eligibility (ineligible when locked)
        const playSpinStatus = rewardStatusRawData?.[WalletTxMethod.PlaySpin];
        const isPlaySpinLocked = !!playSpinStatus?.enabled && !!playSpinStatus?.lockedInstructions;
        const isPlaySpinEligible = !isPlaySpinLocked;

        // For prizes page, check if all relevant promotions are eligible
        // For daily/play-and-spin pages, check the relevant one
        if (pathname === Paths.PRIVATE.PRIZES) {
          // For prizes page, we want to continue polling until all checked promotions become eligible
          // Check if both daily bonus and play-and-spin are eligible
          rewardEligible = isDailyBonusEligible && isPlaySpinEligible;
        } else if (pathname === Paths.PRIVATE.PROMOTION_DAILY_LOGIN) {
          rewardEligible = isDailyBonusEligible;
        } else if (pathname === Paths.PRIVATE.PROMOTION_PLAY_AND_SPIN) {
          rewardEligible = isPlaySpinEligible;
        }
      }

      if (strategy.refetchReferral && referralRewardStatusData) {
        // Referral reward is eligible when enabled and canParticipate is true
        const isReferralRewardAvailable =
          !!referralRewardStatusData?.enabled && !!referralRewardStatusData?.canParticipate;
        referralEligible = isReferralRewardAvailable;
      }

      return { rewardEligible, referralEligible };
    },
    [rewardStatusRawData, referralRewardStatusData, location.pathname],
  );

  const performRefetch = useCallback(
    async (strategy: { refetchReward: boolean; refetchReferral: boolean }) => {
      const promises: Promise<unknown>[] = [];
      let rewardPromise: Promise<{ data?: typeof rewardStatusRawData }> | null = null;
      let referralPromise: Promise<{ data?: typeof referralRewardStatusData }> | null = null;

      if (strategy.refetchReward) {
        const result = refetchRewardStatus();
        if (result) {
          promises.push(result);
          rewardPromise = result as Promise<{ data?: typeof rewardStatusRawData }>;
        }
      }
      if (strategy.refetchReferral) {
        const result = refetchReferralRewardStatus();
        if (result) {
          promises.push(result);
          referralPromise = result as Promise<{ data?: typeof referralRewardStatusData }>;
        }
      }
      await Promise.allSettled(promises);

      const rewardResult = rewardPromise ? await rewardPromise : undefined;
      const referralResult = referralPromise ? await referralPromise : undefined;

      return {
        rewardData: rewardResult?.data,
        referralData: referralResult?.data,
      };
    },
    [refetchRewardStatus, refetchReferralRewardStatus],
  );

  const clearAllTimers = useCallback(() => {
    if (initialTimeoutRef.current) {
      clearTimeout(initialTimeoutRef.current);
      initialTimeoutRef.current = null;
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  const onComplete = useCallback(() => {
    // Clear any existing timers to prevent multiple pending refetches
    clearAllTimers();

    const strategy = getRefetchStrategy();

    // If no refetch needed for this route, skip polling
    if (!strategy.refetchReward && !strategy.refetchReferral) {
      return;
    }

    // Check initial eligibility state
    const initialEligibility = checkEligibility(strategy);

    // If already eligible, skip polling
    if (
      (!strategy.refetchReward || initialEligibility.rewardEligible) &&
      (!strategy.refetchReferral || initialEligibility.referralEligible)
    ) {
      return;
    }

    // Initial refetch after delay
    initialTimeoutRef.current = setTimeout(async () => {
      const refetchResult = await performRefetch(strategy);

      // Check if eligible after initial refetch using fresh data (page-specific check)
      const isNowEligible = checkPageSpecificEligibility(refetchResult.rewardData, refetchResult.referralData);

      if (isNowEligible) {
        clearAllTimers();
        return;
      }

      // Start polling every 1 second
      let pollCount = 0;

      pollingIntervalRef.current = setInterval(async () => {
        pollCount++;
        const refetchResult = await performRefetch(strategy);

        // Check if eligible after refetch using fresh data (page-specific check)
        const isNowEligible = checkPageSpecificEligibility(refetchResult.rewardData, refetchResult.referralData);

        // Stop polling if eligible or reached max polls
        if (isNowEligible || pollCount >= MAX_POLLS) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          if (isNowEligible) {
            clearAllTimers();
          }
          return;
        }
      }, POLLING_INTERVAL_MS);

      // Safety timeout to ensure polling stops after POLLING_DURATION_MS
      pollingTimeoutRef.current = setTimeout(() => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        pollingTimeoutRef.current = null;
      }, POLLING_DURATION_MS);

      initialTimeoutRef.current = null;
    }, PROMOTION_STATUS_REFETCH_DELAY_MS);
  }, [getRefetchStrategy, performRefetch, clearAllTimers, checkEligibility, checkPageSpecificEligibility]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);

  return {
    onComplete,
  };
}
